import type { AuthorId, Channel, ChannelId, Emoji, EmojiId, EntityBundle, Profile } from './types';

/** Entity store configuration; viewer id is required for viewer-relative reaction projection. */
export interface EntityStoreOptions {
  readonly viewerProfileId: AuthorId;
}

/** Entity ids whose stored records changed after an ingest. */
export interface EntityChangeSet {
  readonly profiles: Set<AuthorId>;
  readonly channels: Set<ChannelId>;
  readonly emoji: Set<EmojiId>;
}

/** In-memory entity cache for projecting wire messages into render rows. */
export class EntityStore {
  readonly viewerProfileId: AuthorId;

  private readonly profiles = new Map<AuthorId, Profile>();
  private readonly channels = new Map<ChannelId, Channel>();
  private readonly emoji = new Map<EmojiId, Emoji>();

  /** Starts empty for the required viewer id; callers must ingest entities before lookups resolve. */
  constructor(options: EntityStoreOptions) {
    this.viewerProfileId = options.viewerProfileId;
  }

  /** Upserts an entity bundle and returns ids whose content changed. */
  ingest(bundle: EntityBundle): EntityChangeSet {
    const changes = emptyEntityChangeSet();

    for (const profile of bundle.profiles) {
      if (!sameProfile(this.profiles.get(profile.id), profile)) {
        changes.profiles.add(profile.id);
      }
      this.profiles.set(profile.id, { ...profile });
    }
    for (const channel of bundle.channels) {
      if (!sameChannel(this.channels.get(channel.id), channel)) {
        changes.channels.add(channel.id);
      }
      this.channels.set(channel.id, { ...channel });
    }
    for (const emoji of bundle.emoji) {
      if (!sameEmoji(this.emoji.get(emoji.id), emoji)) {
        changes.emoji.add(emoji.id);
      }
      this.emoji.set(emoji.id, { ...emoji });
    }

    return changes;
  }

  /** Returns the cached profile snapshot, or undefined when not loaded. */
  getProfile(id: AuthorId): Profile | undefined {
    return this.profiles.get(id);
  }

  /** Returns the cached channel snapshot, or undefined when not loaded. */
  getChannel(id: ChannelId): Channel | undefined {
    return this.channels.get(id);
  }

  /** Returns the cached emoji snapshot, or undefined when not loaded. */
  getEmoji(id: EmojiId): Emoji | undefined {
    return this.emoji.get(id);
  }
}

/** Creates an empty change-set with fresh mutable sets. */
export function emptyEntityChangeSet(): EntityChangeSet {
  return {
    profiles: new Set(),
    channels: new Set(),
    emoji: new Set()
  };
}

/** Returns true when any entity kind changed. */
export function hasEntityChanges(changes: EntityChangeSet): boolean {
  return changes.profiles.size > 0 || changes.channels.size > 0 || changes.emoji.size > 0;
}

function sameProfile(left: Profile | undefined, right: Profile): boolean {
  return Boolean(
    left &&
    left.id === right.id &&
    left.name === right.name &&
    left.accent === right.accent &&
    left.avatarUrl === right.avatarUrl
  );
}

function sameChannel(left: Channel | undefined, right: Channel): boolean {
  return Boolean(left && left.id === right.id && left.name === right.name);
}

function sameEmoji(left: Emoji | undefined, right: Emoji): boolean {
  return Boolean(
    left &&
    left.id === right.id &&
    left.shortcode === right.shortcode &&
    left.glyph === right.glyph
  );
}
